/**
 * @file components/certified-orgs/AllClientsRosterTable.tsx
 * @description 全部註冊客戶名錄（序95）。
 *
 * 「新申請＋結束、終止…狀態的客戶（所有已具驗證註冊碼的客戶資料清單）也要有名錄」——
 * 與獲證組織名錄（僅持證中）互補：本表列出全量客戶（唯讀名錄式展示），
 * 資料來源 GET /api/settings/clients（需 client.manage，故僅管理組可見本視圖）。
 *
 * 序221 修復：原版一次拉 pageSize=100 到前端做本地過濾＋本地分頁，客戶數超過 100 會
 * 靜默漏搜；「匯出」則掛在父頁「獲證組織名錄」的 category 篩選上，本頁點擊必失敗。
 * 改為真正的服務端分頁＋搜尋（CLAUDE.md §5），並接上本頁專屬的匯出端點。
 *
 * 序221 增強：新增勾選（`selectedRowKeys` 累積跨頁保存，服務端分頁下不能只存當頁 row
 * 物件），有勾選時匯出僅勾選的客戶（帶 `ids`），無勾選時維持原本「匯出目前篩選結果全部」。
 */

import { useState } from 'react';
import { Button, Input, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { apiClient } from '@/lib/api-client';

/** 客戶名錄行（取 clients 列表回傳的核心欄位） */
interface ClientRosterRow {
  id: number;
  clientCode: string;
  name: string;
  scheme: string;
  verificationStatus: string;
  personInChargeName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactAddress: string | null;
  registeredAddress: string | null;
  createdAt: string;
}

/** 驗證狀態 → Tag 顏色 */
const STATUS_COLOR: Record<string, string> = {
  新申請: 'blue',
  維持: 'green',
  恢復驗證: 'green',
  結束驗證: 'default',
  終止驗證: 'red',
  撤銷驗證: 'red',
  暫時終止驗證_TOP: 'orange',
  暫時停止驗證_TAP: 'orange',
};

export function AllClientsRosterTable() {
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  /** 跨頁累積的勾選客戶 ID（服務端分頁下不能只存當頁 row 物件） */
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data, isLoading } = useQuery<{ items: ClientRosterRow[]; total: number }>({
    // page/pageSize/search 皆入 queryKey，確保切頁/搜尋會重新請求（CLAUDE.md §5）
    queryKey: ['all-clients-roster', page, pageSize, keyword],
    queryFn: () => {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      const kw = keyword.trim();
      if (kw) qs.set('search', kw);
      return apiClient.get(`/api/settings/clients?${qs.toString()}`);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns: ColumnsType<ClientRosterRow> = [
    {
      title: '驗證註冊碼',
      dataIndex: 'clientCode',
      width: 130,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    { title: '農產品經營者名稱', dataIndex: 'name', width: 180, ellipsis: true },
    { title: '驗證方案', dataIndex: 'scheme', width: 90, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: '驗證狀態',
      dataIndex: 'verificationStatus',
      width: 130,
      render: (v: string) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: '負責人',
      dataIndex: 'personInChargeName',
      width: 100,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '聯絡人 / 電話',
      key: 'contact',
      width: 160,
      render: (_, r) => [r.contactName, r.contactPhone].filter(Boolean).join(' / ') || '—',
    },
    {
      title: '通訊地址',
      dataIndex: 'contactAddress',
      width: 200,
      ellipsis: true,
      render: (v: string | null, r) => v ?? r.registeredAddress ?? '—',
    },
    {
      title: '建檔時間',
      dataIndex: 'createdAt',
      width: 110,
      render: (v: string) => new Date(v).toLocaleDateString('zh-TW'),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <Input
          allowClear
          placeholder="搜尋 名稱 / 註冊碼"
          style={{ width: 280 }}
          value={keyword}
          onChange={(e) => {
            setPage(1);
            setKeyword(e.target.value);
          }}
        />
        <Button
          icon={<Download size={14} />}
          onClick={() => {
            const p = new URLSearchParams();
            if (selectedIds.length) {
              p.set('ids', selectedIds.join(','));
            } else if (keyword.trim()) {
              p.set('search', keyword.trim());
            }
            const qs = p.toString();
            window.location.href = `/api/settings/clients/export.xlsx${qs ? `?${qs}` : ''}`;
          }}
        >
          {selectedIds.length ? `匯出已選 ${selectedIds.length} 筆` : '匯出 Excel'}
        </Button>
      </div>
      <Table<ClientRosterRow>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={items}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        // 表頭吸附應用頂欄下方、橫向捲軸吸附視窗底部常駐（避免捲軸隱藏時無法橫向捲動）
        sticky={{ offsetHeader: 56 }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys as number[]),
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showTotal: (t) => `共 ${t} 筆`,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
}
